import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import type { TagEntity } from '@schibsted/frapi-schema';
import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
} from '../../../../../types/frapi-types.js';
import type { SaveListItem } from '../../../../../types/save-types.js';

import { useIntersectionObserver } from '../../../../hooks/useIntersectionObserver.js';
import { useLocalStorage } from '../../../../hooks/useLocalStorage.js';
import { useListsContext } from '../../../saving/ListsProvider.js';
import { itemIsSaved } from '../../../saving/utils.js';
import TeaserGrade from '../../../teasers/teaserComponents/TeaserGrade.js';
import {
  containsTagOfType,
  getPriceLabel,
  isRuterExcludedLocation,
} from '../../helpers.js';
import IconPill from '.././IconPill.js';
import SaveArticleReminder from '.././SaveArticleReminder.js';
import SaveButton from '.././SaveButton.js';
import Tags from '.././Tags.js';
import UrlButtons from './UrlButtons.js';
import { usePulseContext } from '../../../../../../core/mandatory-integrations/pulse/PulseProvider.js';

export type AboutProps = {
  article: ByfunnFrapiArticle | ByfunnPromoArticle;
  handleOverlay: (item: SaveListItem) => void;
  item: SaveListItem;
  isMobile?: boolean;
  isRestaurantReview?: boolean;
  enableRuterCampaign: boolean;
};

export const getLocationTag = (tags: TagEntity[]) => {
  const tagsArray = tags.filter((tag) => tag.title.startsWith('Vink/område'));
  const tagTitle = tagsArray[0]?.title.split('/')[2];
  const tagSlug = '/tag/' + tagsArray[0]?.slug;

  return tagsArray.length > 0 ? <a href={tagSlug}>, {tagTitle}</a> : '';
};

const About: React.FC<AboutProps> = ({
  article,
  handleOverlay,
  item,
  isMobile,
  isRestaurantReview = true,
  enableRuterCampaign,
}) => {
  const { tags } = article;
  const { trackClickUIElement } = usePulseContext();
  const { savedItems, isLoading } = useListsContext();
  const [isSaved, setIsSaved] = useState(false);
  const [showSaveReminder, setShowSaveReminder] = useLocalStorage(
    'showSaveReminder',
    true,
  );
  const locationPillRef = useRef<HTMLDivElement>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  useIntersectionObserver(locationPillRef, () => setHasEnteredView(true));

  const isRuterCampaignActive =
    enableRuterCampaign && !!article.ruterUrl && !isRuterExcludedLocation(tags);

  useEffect(() => {
    if (savedItems.length > 0) {
      setIsSaved(itemIsSaved(item, savedItems));
    }
  }, [item, savedItems]);

  const handleHideReminder = () => {
    trackClickUIElement(
      {
        object: {
          id: 'article:hide-save-reminder',
          name: 'Hide save reminder',
          elementType: 'Button',
        },
      },
      {
        object: {
          objectId: 'hide-save-reminder',
          name: 'Hide save reminder',
          elementType: 'Button',
        },
        placement: {
          containerName: 'page',
        },
      },
    );

    setShowSaveReminder(false);
  };

  const handleOnClick = () => {
    trackClickUIElement(
      {
        object: {
          id: `article:save-button`,
          name: `Save ${isRestaurantReview ? 'restaurant' : 'movie'} article`,
          elementType: 'Button',
        },
      },
      {
        object: {
          objectId: 'save-button',
          name: `Save ${isRestaurantReview ? 'restaurant' : 'movie'} article`,
          elementType: 'Button',
        },
        placement: {
          containerName: 'page',
        },
      },
    );

    handleOverlay(item);
    if (showSaveReminder) {
      setShowSaveReminder(false);
    }
  };

  const shouldRenderSaveReminder = showSaveReminder && !isSaved && !isLoading;

  const buttons = (children?: React.ReactNode) => (
    <div
      className={clsx('buttons', {
        'with-ruter-button': isRuterCampaignActive,
      })}
    >
      {children}
      <div className="save-container">
        <SaveButton
          handleOnClick={handleOnClick}
          item={item}
          isSaved={isSaved}
          disabled={isLoading}
          condensed={isRuterCampaignActive && !!isMobile}
        />
        {shouldRenderSaveReminder && (
          <SaveArticleReminder handleClose={handleHideReminder} />
        )}
      </div>
    </div>
  );

  const restaurantInfo = (
    <div className="about">
      {containsTagOfType(tags, 'område') && (
        <div
          className={clsx('location-pill', {
            'location-pill--animate': hasEnteredView,
          })}
          ref={locationPillRef}
        >
          <IconPill icon="location" inverted large>
            <Tags tags={tags} type={'område'} noBorder />
          </IconPill>
        </div>
      )}
      <div className="header-container">
        <h2 className="title">{article?.restaurantTitle}</h2>
        {article.grade && <TeaserGrade grade={article.grade} />}
      </div>
      <div className="tag-container tag-container--restaurant">
        {containsTagOfType(tags, 'kjøkken') && (
          <IconPill icon="cuisine">
            <Tags tags={tags} type={'kjøkken'} limit={2} noBorder />
          </IconPill>
        )}
        {containsTagOfType(tags, 'mattype') && (
          <IconPill icon="food-type">
            <Tags tags={tags} type={'mattype'} noBorder />
          </IconPill>
        )}
        {article.priceLevel && article.priceLevel !== 'none' && (
          <IconPill icon="wallet">{getPriceLabel(article.priceLevel)}</IconPill>
        )}
      </div>
      {buttons(
        <UrlButtons
          article={article}
          compact={!!isMobile}
          renderRuterButton={isRuterCampaignActive}
        />,
      )}
    </div>
  );

  const movieAbout = (
    <div className="about movie">
      <div className="header-container">
        <h2 className="title">{article?.restaurantTitle}</h2>
        {article.grade && <TeaserGrade grade={article.grade} />}
      </div>
      <div className="tag-container tag-container--movie">
        <Tags tags={tags} type="strømmetjeneste" noBorder />
        <div className="tag-separator"></div>
        <Tags tags={tags} type={['format']} noBorder regular />
        <div className="tag-separator"></div>
        <Tags tags={tags} type={'type'} limit={2} noBorder regular />
      </div>
      {buttons()}
    </div>
  );

  return isRestaurantReview ? restaurantInfo : movieAbout;
};

export default About;
